import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useApiData } from '../api/client';
import { type GraphData, type GraphNode, type GraphEdge, type GraphSchema, type UndoableTransaction, type UndoResponse, type UndoConflict } from '../api/dastarkhan';
import { useDastarkhanApi } from '../api/dastarkhan';

interface CytoscapeElement {
  data: Record<string, unknown>;
  group: 'nodes' | 'edges';
}

export function useGraph(projectId: string) {
  const graphEndpoint = `/dastarkhan/projects/${projectId}/graph?limit=10000`;
  const schemaEndpoint = `/dastarkhan/projects/${projectId}/graph/schema`;
  const { data: graphData, loading: graphLoading, error: graphError, refetch: refetchGraph } = useApiData<GraphData>(graphEndpoint);
  const { data: schemaData, refetch: refetchSchema } = useApiData<GraphSchema>(schemaEndpoint);

  const nodes = graphData?.nodes || [];
  const edges = graphData?.edges || [];
  const totalNodes = graphData?.total_nodes || 0;
  const totalEdges = graphData?.total_edges || 0;
  const truncated = graphData?.truncated || false;
  const loading = graphLoading;
  const error = graphError ? 'Failed to load graph' : null;
  const schema = schemaData || null;

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [queryResults, setQueryResults] = useState<GraphData | null>(null);
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());

  // Undo state
  const [lastUndoable, setLastUndoable] = useState<UndoableTransaction | null>(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const { getLastUndoable, executeUndo } = useDastarkhanApi();
  const getLastUndoableRef = useRef(getLastUndoable);
  getLastUndoableRef.current = getLastUndoable;
  const executeUndoRef = useRef(executeUndo);
  executeUndoRef.current = executeUndo;

  const fetchLastUndoable = useCallback(async () => {
    try {
      const result = await getLastUndoableRef.current(projectId);
      setLastUndoable(result ?? null);
    } catch {
      setLastUndoable(null);
    }
  }, [projectId]);

  const refetch = useCallback(() => {
    refetchGraph();
    refetchSchema();
    fetchLastUndoable();
  }, [refetchGraph, refetchSchema, fetchLastUndoable]);

  // Fetch last undoable on mount and when projectId changes
  useEffect(() => {
    fetchLastUndoable();
  }, [fetchLastUndoable]);

  const handleUndo = useCallback(async (transactionId: number, force = false): Promise<UndoResponse | UndoConflict | undefined> => {
    setUndoLoading(true);
    try {
      const result = await executeUndoRef.current(projectId, transactionId, force);
      if (result && result.status === 'success') {
        refetchGraph();
        refetchSchema();
        await fetchLastUndoable();
      }
      return result;
    } finally {
      setUndoLoading(false);
    }
  }, [projectId, refetchGraph, refetchSchema, fetchLastUndoable]);

  const handleQueryResult = useCallback((result: GraphData) => {
    setQueryResults(result);
    const ids = new Set<string>();
    result.nodes.forEach((n) => ids.add(n.element_id));
    result.edges.forEach((e) => ids.add(e.element_id));
    setHighlightedIds(ids);
  }, []);

  const clearQuery = useCallback(() => {
    setQueryResults(null);
    setHighlightedIds(new Set());
  }, []);

  // Convert to Cytoscape elements — memoized so the reference is stable
  // when the underlying graphData hasn't changed (prevents GraphCanvas re-layout on unrelated re-renders).
  // Orphan edges (endpoints not in the returned node set) are dropped with a warning
  // so a single bad edge can't crash the canvas through the ErrorBoundary.
  const cytoscapeElements: CytoscapeElement[] = useMemo(() => {
    const nodeIds = new Set(nodes.map((n) => n.element_id));
    const orphans: GraphEdge[] = [];
    const liveEdges = edges.filter((e) => {
      const hasSrc = nodeIds.has(e.source_element_id);
      const hasTgt = nodeIds.has(e.target_element_id);
      if (hasSrc && hasTgt) return true;
      orphans.push(e);
      return false;
    });
    if (orphans.length > 0) {
      console.warn(
        `[useGraph] Dropped ${orphans.length} orphan edge(s) whose endpoints are not in the returned node set.`,
        orphans.slice(0, 5).map((e) => ({
          edge_id: e.element_id,
          source: e.source_element_id,
          target: e.target_element_id,
          type: e.type,
        })),
      );
    }
    return [
      ...nodes.map((n) => ({
        data: {
          // Spread properties FIRST so a stored `id` / `label` attribute
          // can never clobber the Cytoscape-required fields below.
          ...n.properties,
          id: n.element_id,
          label: (n.properties.name as string) || n.labels[0] || 'Node',
          _entityType: n.labels[0] || 'Unknown',
          _jurisdiction: (n.properties.jurisdiction as string) || 'Unknown',
          _labels: n.labels,
          _element_id: n.element_id,
        },
        group: 'nodes' as const,
      })),
      ...liveEdges.map((e) => ({
        data: {
          ...e.properties,
          id: e.element_id,
          source: e.source_element_id,
          target: e.target_element_id,
          label: e.type,
          _type: e.type,
          _element_id: e.element_id,
        },
        group: 'edges' as const,
      })),
    ];
  }, [nodes, edges]);

  return {
    nodes,
    edges,
    totalNodes,
    totalEdges,
    truncated,
    loading,
    error,
    selectedNode,
    selectedEdge,
    setSelectedNode,
    setSelectedEdge,
    schema,
    queryResults,
    highlightedIds,
    handleQueryResult,
    clearQuery,
    refetch,
    cytoscapeElements,
    lastUndoable,
    undoLoading,
    handleUndo,
  };
}
